import { store } from "src/store/store";
import { Message } from "src/modules/communication-protobuf";
import { changeMotionSensitivity } from "src/modules/baby-station/duck/actions";
import { getMotionSensitivity } from "src/modules/baby-station/duck/selectors";
import motionSensitivity, {
  averageThresholds
} from "./motionSensitivityOptions";
import environment from "src/utils/environment";

let cv = window.require("opencv4js");

const MOTION_INTERVAL = 500; // 2 fps

export default class MotionDetection {
  constructor({
    getEventManager,
    getPeers,
    messenger,
    getActivityEmitter,
    handleDetectionAvailabilityChange,
    handleMotionIndication,
    notifyAboutMotionAvailabilityChange
  }) {
    this.notifyAboutMotionAvailabilityChange =
      notifyAboutMotionAvailabilityChange;
    this.handleDetectionAvailabilityChange = handleDetectionAvailabilityChange;
    this.handleMotionIndication = handleMotionIndication;
    this.messenger = messenger;
    this.getEventManager = getEventManager;
    this.getActivityEmitter = getActivityEmitter;
    this.getPeers = getPeers;
    this.eventManager = null;
    this.isMonitoring = false;
    this.sensitivity = getMotionSensitivity(store.getState());
    this.interval = null;

    this.isMotion = false;
    this.canDetect = true;

    this.motionFrames = 0;
    this.motionStartDate = 0;
    this.motionEventStartDate = 0;

    if (this.sensitivity !== "MD_OFF") this.startMotionDetection();
  }

  monitoringStarted = () => {
    this.eventManager = this.getEventManager();
    this.isMonitoring = true;
  };

  monitoringEnded = () => {
    this.isMonitoring = false;
  };

  handleMotionSensitivityChange = (sensitivity) => {
    if (sensitivity === "MD_OFF") {
      this.stopMotionDetection();
      this.isMonitoring &&
        this.notifyAboutMotionAvailabilityChange({
          motionState: "NOT_RUNNING",
          motionSensitivity: sensitivity
        });
    } else {
      this.isMonitoring &&
        this.notifyAboutMotionAvailabilityChange({
          motionState: this.canDetect
            ? "RUNNING"
            : "STOPPED_REASON_LOW_CAMERA_EXPOSURE",
          motionSensitivity: sensitivity
        });
    }
    this.sensitivity = sensitivity;
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.startMotionDetection();
    store.dispatch(changeMotionSensitivity(sensitivity));
  };

  handleMotionDetectionIndication = ({ isMotion }) => {
    if (isMotion) this.handleMotionIndication(true);
    else this.handleMotionIndication(false);
  };

  startMotionDetection = async () => {
    if (environment.isBrowser) {
      cv = await window.cv;
    }

    const video = window.videoRef.current;
    video.height = video.offsetHeight;
    video.width = video.offsetWidth;

    const cap = new cv.VideoCapture(video);
    let refFrame = null;
    let averageMotion = 0;
    this.interval = setInterval(() => {
      try {
        // (360p, 720 width)
        const scaleFactor = 360 / 720;
        const mat = new cv.Mat(video.height, video.width, cv.CV_8UC4);
        cap.read(mat);

        const gray = new cv.Mat();

        cv.resize(mat, gray, new cv.Size(0, 0), scaleFactor, scaleFactor);
        cv.cvtColor(gray, gray, cv.COLOR_RGBA2GRAY);

        // detect if the frame is too dark
        const nonZeroElements = cv.countNonZero(gray);
        const elements = gray.rows * gray.cols;
        const brightness = Math.round((nonZeroElements / elements) * 100);

        if (brightness <= 10) {
          this.canDetect = false;
          this.handleDetectionAvailabilityChange({ canDetectMotion: false });
          return;
        } else {
          this.canDetect = true;
          this.handleDetectionAvailabilityChange({ canDetectMotion: true });
        }

        if (!refFrame) {
          refFrame = new cv.Mat();
          gray.copyTo(refFrame);
        }

        const diffFrame = new cv.Mat();
        cv.absdiff(gray, refFrame, diffFrame);

        const threshFrame = new cv.Mat();

        cv.threshold(
          diffFrame,
          threshFrame,
          motionSensitivity[this.sensitivity].diffThreshold,
          255,
          cv.THRESH_BINARY
        );

        const newAverageMotion = cv.countNonZero(threshFrame);
        averageMotion = 0.8 * averageMotion + 0.2 * newAverageMotion;
        const detectedMotionAverage = averageMotion / elements;

        if (detectedMotionAverage > averageThresholds[this.sensitivity]) {
          this.onMotion();
        } else this.onRest();

        gray.copyTo(refFrame);
        mat.delete();
        gray.delete();
        diffFrame.delete();
        threshFrame.delete();
      } catch (err) {
        this.stopMotionDetection();
      }
    }, MOTION_INTERVAL);
  };

  stopMotionDetection = () => {
    clearInterval(this.interval);
    this.interval = null;
  };

  onMotion = () => {
    if (this.motionFrames < 2) {
      this.motionFrames += 1;

      return;
    }
    this.isMotion = true;
    const activityEmitter = this.getActivityEmitter();
    if (activityEmitter) activityEmitter.handleMotion();
    this.handleMotionDetectionIndication({ isMotion: true });

    if (this.isMonitoring) {
      const peers = this.getPeers();
      const message = Message.MotionInfoPacket.create();
      Object.values(peers).forEach(({ jid }) => {
        this.messenger.sendAsEnvelope({
          to: jid,
          payload: { message }
        });
      });
    }
    this.motionStartDate = Date.now();

    if (this.motionEventStartDate + 10000 > Date.now()) {
      return;
    }
    this.motionEventStartDate = Date.now();
    if (this.isMonitoring) this.eventManager.onMotion();
  };

  onRest = () => {
    if (this.isMotion && this.motionStartDate + 1000 > Date.now()) {
      return;
    }
    this.handleMotionDetectionIndication({ isMotion: false });
    this.motionFrames = 0;
    this.isMotion = false;
  };
}
